import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import { EntitiesService } from '@/services/entities.service';
import { toast } from 'sonner';

interface Client {
  clientId: string;
  clientName: string;
  entityCount?: number; // Number of entities this client has
  entities?: Entity[]; // Include entities from clients summary
}

interface Entity {
  entityId: string;
  entityName: string;
  type?: string;
  connectionStatus?: string;
  lastSync?: string;
  pending_schedules_count?: number;
}

interface NavigationState {
  // Global selection state
  selectedClientId: string | null;
  selectedEntityId: string | 'all' | null;
  
  // Data
  clients: Client[];
  entities: Entity[];
  
  // Loading states
  isLoadingClients: boolean;
  isLoadingEntities: boolean;
  
  // Actions
  setClientId: (id: string | null) => void;
  setEntityId: (id: string | 'all' | null, pathname?: string) => void;
  setClients: (clients: Client[]) => void;
  setEntities: (entities: Entity[]) => void;
  setLoadingClients: (loading: boolean) => void;
  setLoadingEntities: (loading: boolean) => void;
  
  // Data loading
  loadClients: (forceRefresh?: boolean) => Promise<void>;
  loadEntities: (clientId: string) => Promise<void>;
  
  // Helper methods
  getEntityForPage: (pathname: string) => string | 'all' | null;
  getEntityDisplayName: () => string;
  reset: () => void;
  
  // Prepayments count methods
  getPrepaymentsCount: (entityId?: string | 'all' | null) => number;
  hasPrepaymentsSchedules: (entityId?: string | 'all' | null) => boolean;
}

// Page configuration for entity selection constraints
const PAGE_CONSTRAINTS = {
  '/accpay/prepayments': { supportsAll: false, defaultSelection: 'first' },
  '/accpay/all': { supportsAll: true, defaultSelection: 'all' },
  '/accpay': { supportsAll: true, defaultSelection: 'all' },
  '/compliance': { supportsAll: false, defaultSelection: 'first' },
} as const;

const initialState = {
  selectedClientId: null,
  selectedEntityId: null,
  clients: [],
  entities: [],
  isLoadingClients: false,
  isLoadingEntities: false,
};

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Actions
      setClientId: (id: string | null) => {
        const state = get();
        
        // Find entities for the selected client from already loaded clients data
        const selectedClient = state.clients.find(client => client.clientId === id);
        const clientEntities = selectedClient?.entities || [];
        
        
        set({ 
          selectedClientId: id,
          // Reset entity when client changes
          selectedEntityId: null,
          // Use entities from client summary instead of loading separately
          entities: clientEntities
        });
        
        // Don't call loadEntities() - we already have the data from loadClients()
        // This preserves the pending_schedules_count from the dashboard API
      },

      setEntityId: (id: string | 'all' | null, pathname?: string) => {
        // Use provided pathname or current location
        const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
        
        // Check page constraints
        const constraint = PAGE_CONSTRAINTS[currentPath as keyof typeof PAGE_CONSTRAINTS];
        
        // If page doesn't support 'all' and 'all' was selected
        if (constraint && !constraint.supportsAll && id === 'all') {
          // Auto-select first entity if available
          const entities = get().entities;
          const actualId = entities.length > 0 ? entities[0].entityId : null;
          set({ selectedEntityId: actualId });
        } else {
          set({ selectedEntityId: id });
        }
      },

      setClients: (clients: Client[]) => set({ clients }),
      
      setEntities: (entities: Entity[]) => set({ entities }),
      
      setLoadingClients: (loading: boolean) => set({ isLoadingClients: loading }),
      
      setLoadingEntities: (loading: boolean) => set({ isLoadingEntities: loading }),

      // Data loading
      loadClients: async (forceRefresh = false) => {
        const state = get();
        
        
        // Prevent concurrent requests
        if (state.isLoadingClients) {
          return;
        }
        
        // Skip if already loaded and not forcing refresh
        if (!forceRefresh && state.clients.length > 0) {
          return;
        }
        
        set({ isLoadingClients: true });
        
        try {
          const response = await api.getClientsSummary({ limit: 1000 });
          
          const clients = response.clients.map(client => ({
            clientId: client.client_id,
            clientName: client.name,
            entityCount: client.entities?.length || 0,
            entities: client.entities?.map(entity => ({
              entityId: entity.entity_id,
              entityName: entity.entity_name,
              type: entity.type,
              connectionStatus: entity.connection_status,
              lastSync: entity.last_sync,
              pending_schedules_count: entity.pending_schedules_count
            })) || []
          }));
          
          
          // Prepare state updates
          const updates: Partial<NavigationState> = {
            clients,
            isLoadingClients: false
          };
          
          // Validate existing selections and auto-select if needed
          const currentState = get();
          const currentClientId = currentState.selectedClientId;
          
          // Check if current selection is still valid
          const isCurrentClientValid = currentClientId && clients.find(c => c.clientId === currentClientId);
          const shouldAutoSelect = !currentClientId || !isCurrentClientValid;
          
          
          if (shouldAutoSelect && clients.length > 0) {
            // Prioritize clients that have entities with actual prepayment counts
            const clientsWithCounts = clients.filter(client => 
              client.entities?.some(e => e.pending_schedules_count && e.pending_schedules_count > 0)
            );
            
            const clientWithEntities = clientsWithCounts.length > 0 
              ? clientsWithCounts[0]  // First client with counts
              : clients.find(client => (client.entityCount || 0) > 0); // Fallback to any client with entities
            
            
            if (clientWithEntities) {
              updates.selectedClientId = clientWithEntities.clientId;
              // Set entities from the selected client (preserves pending_schedules_count)
              updates.entities = clientWithEntities.entities || [];
              
              // Auto-select first entity with counts, or first entity as fallback
              const entityWithCounts = updates.entities.find(e => e.pending_schedules_count && e.pending_schedules_count > 0);
              if (entityWithCounts) {
                updates.selectedEntityId = entityWithCounts.entityId;
              } else if (updates.entities.length > 0) {
                updates.selectedEntityId = updates.entities[0].entityId;
              }
              
            }
          } else {
            
            // IMPORTANT: Even if client is already selected, we need to populate entities!
            if (currentClientId && isCurrentClientValid) {
              const existingClient = isCurrentClientValid;
              updates.entities = existingClient.entities || [];
              
              // Also ensure entity is selected if we have entities but no entity selection
              const currentEntityId = get().selectedEntityId;
              if (!currentEntityId && updates.entities.length > 0) {
                // Prioritize entities with counts, fallback to first entity
                const entityWithCounts = updates.entities.find(e => e.pending_schedules_count && e.pending_schedules_count > 0);
                updates.selectedEntityId = entityWithCounts ? entityWithCounts.entityId : updates.entities[0].entityId;
              }
              
            }
          }
          
          // Apply all updates at once with persistence bypass for auto-selection
          
          // First apply data updates
          set({ clients, isLoadingClients: false });
          
          // Then apply auto-selection with a microtask to ensure persistence doesn't override
          if (updates.selectedClientId || updates.selectedEntityId || updates.entities) {
            setTimeout(() => {
              const currentState = get();
              // Verify selections are still valid before applying
              if (updates.selectedClientId && 
                  currentState.clients.find(c => c.clientId === updates.selectedClientId)) {
                set({
                  selectedClientId: updates.selectedClientId,
                  selectedEntityId: updates.selectedEntityId,
                  entities: updates.entities || []
                });
              }
            }, 0);
          }
          
        } catch (error) {
          console.error('Failed to load clients:', error);
          toast.error('Failed to load clients');
          set({ isLoadingClients: false });
        }
      },

      loadEntities: async (clientId: string) => {
        const state = get();
        
        // Prevent concurrent requests for same client
        if (state.isLoadingEntities) {
          return;
        }
        
        set({ isLoadingEntities: true });
        
        try {
          const response = await EntitiesService.getEntitiesForClient(clientId);
          
          const entities = response.entities.map(entity => ({
            entityId: entity.entity_id,
            entityName: entity.entity_name,
            type: entity.type,
            connectionStatus: entity.connection_status,
            lastSync: entity.last_sync,
            pending_schedules_count: entity.pending_schedules_count
          }));
          
          
          const updates: Partial<NavigationState> = {
            entities,
            isLoadingEntities: false
          };
          
          // Auto-select entity if none selected
          const currentState = get();
          if (!currentState.selectedEntityId && entities.length > 0) {
            // Get current page constraints
            const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
            const constraint = PAGE_CONSTRAINTS[pathname as keyof typeof PAGE_CONSTRAINTS];
            
            if (constraint?.supportsAll) {
              updates.selectedEntityId = 'all';
            } else {
              updates.selectedEntityId = entities[0].entityId;
            }
          }
          
          set(updates);
        } catch (error) {
          console.error('Failed to load entities:', error);
          toast.error('Failed to load entities');
          set({ 
            entities: [],
            isLoadingEntities: false 
          });
        }
      },

      // Helper methods
      getEntityForPage: (pathname: string) => {
        const state = get();
        const constraint = PAGE_CONSTRAINTS[pathname as keyof typeof PAGE_CONSTRAINTS];
        
        // If page doesn't support 'all' and 'all' is selected
        if (constraint && !constraint.supportsAll && state.selectedEntityId === 'all') {
          // Return first entity if available
          return state.entities.length > 0 ? state.entities[0].entityId : null;
        }
        
        return state.selectedEntityId;
      },

      getEntityDisplayName: () => {
        const state = get();
        
        if (!state.selectedClientId) return 'No client selected';
        
        const client = state.clients.find(c => c.clientId === state.selectedClientId);
        if (!client) return 'Unknown client';
        
        if (state.selectedEntityId === 'all') {
          return `${client.clientName} - All Entities`;
        }
        
        if (!state.selectedEntityId) return client.clientName;
        
        const entity = state.entities.find(e => e.entityId === state.selectedEntityId);
        if (!entity) return client.clientName;
        
        // Check if client has multiple entities
        if (state.entities.length > 1) {
          return `${client.clientName} | ${entity.entityName}`;
        } else {
          // Single entity - just show client name
          return client.clientName;
        }
      },

      // Prepayments count methods
      getPrepaymentsCount: (entityId?: string | 'all' | null) => {
        const state = get();
        const targetEntityId = entityId || state.selectedEntityId;
        
        
        // Return 0 if no entities loaded or no selection
        if (!state.entities.length || !targetEntityId) {
          return 0;
        }
        
        // Handle "all entities" case - sum across all entities
        if (targetEntityId === 'all') {
          const total = state.entities.reduce((sum, entity) => {
            const count = entity.pending_schedules_count || 0;
            return sum + Math.max(0, Math.floor(Number(count)));
          }, 0);
          return total;
        }
        
        // Handle single entity case
        const entity = state.entities.find(e => e.entityId === targetEntityId);
        if (!entity) {
          return 0;
        }
        
        const count = entity.pending_schedules_count || 0;
        const result = Math.max(0, Math.floor(Number(count)));
        return result;
      },

      hasPrepaymentsSchedules: (entityId?: string | 'all' | null) => {
        return get().getPrepaymentsCount(entityId) > 0;
      },

      reset: () => set(initialState),
    }),
    {
      name: 'navigation-store',
      partialize: (state) => ({
        // Only persist selections, not data
        selectedClientId: state.selectedClientId,
        selectedEntityId: state.selectedEntityId,
      }),
    }
  )
);

// Export convenience hooks for backward compatibility
export const useAccpaySelections = () => {
  const store = useNavigationStore();
  return {
    selectedClientId: store.selectedClientId,
    selectedEntityId: store.selectedEntityId,
    selectedStatusFilters: [], // Removed - handle separately if needed
    setClientId: store.setClientId,
    setEntityId: store.setEntityId,
    setStatusFilters: () => {}, // No-op for now
    setSelections: (clientId: string | null, entityId: string | 'all') => {
      store.setClientId(clientId);
      if (clientId) {
        store.setEntityId(entityId);
      }
    },
  };
};

export const useBillsAmortizationSelections = () => {
  const store = useNavigationStore();
  const pathname = '/accpay/prepayments';
  
  return {
    selectedClientId: store.selectedClientId,
    selectedEntityId: store.getEntityForPage(pathname),
    selectedStatusFilters: [], // Removed - handle separately if needed
    setClientId: store.setClientId,
    setEntityId: store.setEntityId,
    setStatusFilters: () => {}, // No-op for now
    setSelections: (clientId: string | null, entityId: string | null) => {
      store.setClientId(clientId);
      if (clientId) {
        store.setEntityId(entityId);
      }
    },
  };
};